import React, { useEffect, useRef, useState } from "react";
import {
  Dimensions,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../constants/fonts";

export interface TeamAssignmentFilterOption {
  id: string | number;
  displayText: string;
}

interface TeamAssignmentFilterDropdownProps {
  label: string;
  options: TeamAssignmentFilterOption[];
  value: TeamAssignmentFilterOption[];
  onChange: (value: TeamAssignmentFilterOption[] | null) => void;
  onOpen?: () => void;
}

const MENU_HEIGHT = 320;
const MIN_MENU_WIDTH = 220;

const TeamAssignmentFilterDropdown: React.FC<TeamAssignmentFilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  onOpen,
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<any>(null);
  const selected = value ?? [];
  const query = searchText.trim().toLowerCase();
  const filteredOptions = query
    ? options
        .filter((option) => option.displayText.toLowerCase().includes(query))
        .sort((a, b) => a.displayText.localeCompare(b.displayText))
    : options;

  const isSelected = (option: TeamAssignmentFilterOption) =>
    selected.some((item) => String(item.id).toLowerCase() === String(option.id).toLowerCase());

  const updatePosition = () => {
    const { height: screenHeight, width: screenWidth } = Dimensions.get("window");
    const getSafePosition = (x: number, width: number, top: number) => {
      const menuWidth = Math.min(
        Math.max(width || MIN_MENU_WIDTH, MIN_MENU_WIDTH),
        Math.max(screenWidth - 16, 0)
      );
      const left = Math.min(Math.max(x, 8), Math.max(screenWidth - menuWidth - 8, 8));
      return { top, left, width: menuWidth };
    };

    if (Platform.OS === "web" && triggerRef.current?.getBoundingClientRect) {
      const rect = triggerRef.current.getBoundingClientRect();
      const top = screenHeight - rect.bottom < MENU_HEIGHT + 8 ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4;
      setDropPos(getSafePosition(rect.left, rect.width, top));
      return;
    }

    if (triggerRef.current?.measureInWindow) {
      triggerRef.current.measureInWindow((x: number, y: number, width: number, height: number) => {
        const top = screenHeight - (y + height) < MENU_HEIGHT + 8 ? y - MENU_HEIGHT - 4 : y + height + 4;
        setDropPos(getSafePosition(x, width, top));
      });
    }
  };

  useEffect(() => {
    if (!open || Platform.OS !== "web") return undefined;
    const handlePositionChange = () => updatePosition();
    window.addEventListener("scroll", handlePositionChange, true);
    window.addEventListener("resize", handlePositionChange);
    updatePosition();
    return () => {
      window.removeEventListener("scroll", handlePositionChange, true);
      window.removeEventListener("resize", handlePositionChange);
    };
  }, [open]);

  const handleOpen = () => {
    onOpen?.();
    if (!open) updatePosition();
    setOpen((current) => !current);
    if (open) setSearchText("");
  };

  const handleSelect = (option: TeamAssignmentFilterOption) => {
    const next = isSelected(option)
      ? selected.filter((item) => String(item.id) !== String(option.id))
      : [...selected, option];
    onChange(next.length > 0 ? next : null);
  };

  const handleSelectAll = () => {
    const visibleIds = new Set(filteredOptions.map((option) => String(option.id)));
    const hiddenSelected = selected.filter((option) => !visibleIds.has(String(option.id)));
    const next = [...hiddenSelected, ...filteredOptions];
    onChange(next.length > 0 ? next : null);
  };

  const handleClear = () => {
    onChange(null);
    setSearchText("");
  };

  const close = () => {
    setOpen(false);
    setSearchText("");
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        ref={triggerRef}
        accessibilityLabel={`Filter ${label}`}
        accessibilityRole="button"
        style={[styles.trigger, selected.length > 0 && styles.triggerActive]}
        onPress={handleOpen}
        activeOpacity={0.7}
      >
        <MaterialIcons name="filter-list" size={14} color={selected.length > 0 ? "#005CBC" : "#838383"} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={close}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={close}>
          <TouchableOpacity
            activeOpacity={1}
            style={[
              styles.menu,
              {
                top: dropPos.top,
                left: dropPos.left,
                width: dropPos.width || MIN_MENU_WIDTH,
              },
            ]}
            onPress={(event) => event.stopPropagation()}
          >
            <View style={styles.header}>
              <Text style={styles.title}>{label} Filter</Text>
              <View style={styles.searchRow}>
                <MaterialIcons name="search" size={17} color="#7C8086" />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search..."
                  placeholderTextColor="#9AA1AB"
                  value={searchText}
                  onChangeText={setSearchText}
                />
                {searchText ? (
                  <TouchableOpacity onPress={() => setSearchText("")}>
                    <MaterialIcons name="close" size={16} color="#6D737C" />
                  </TouchableOpacity>
                ) : null}
              </View>
              <View style={styles.actionsRow}>
                <TouchableOpacity onPress={handleSelectAll} style={styles.actionButton}>
                  <Text style={styles.actionText}>Select All</Text>
                </TouchableOpacity>
                <View style={styles.divider} />
                <TouchableOpacity onPress={handleClear} style={styles.actionButton}>
                  <Text style={styles.actionText}>Clear</Text>
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView style={styles.list} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {filteredOptions.length === 0 ? (
                <Text style={styles.emptyText}>No options found</Text>
              ) : (
                filteredOptions.map((option) => {
                  const checked = isSelected(option);
                  return (
                    <TouchableOpacity
                      key={`${String(option.id)}-${option.displayText}`}
                      style={[styles.option, checked && styles.optionSelected]}
                      onPress={() => handleSelect(option)}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                        {checked && <MaterialIcons name="check" size={13} color="#FFFFFF" />}
                      </View>
                      <Text style={styles.optionText} numberOfLines={1}>
                        {option.displayText}
                      </Text>
                    </TouchableOpacity>
                  );
                })
              )}
            </ScrollView>

            <View style={styles.footer}>
              <Text style={styles.footerText}>
                {selected.length} of {options.length} selected
              </Text>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { alignSelf: "center" },
  trigger: {
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
  },
  triggerActive: { backgroundColor: "#EAF3FF" },
  overlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.15)" },
  menu: {
    position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
    maxHeight: Dimensions.get("window").height * 0.45,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    zIndex: 9999,
    elevation: 8,
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 4px 16px rgba(0,0,0,0.16)" } as any)
      : { shadowColor: "#000", shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.16, shadowRadius: 10 }),
  },
  header: { borderBottomWidth: 1, borderBottomColor: "#E9ECEF" },
  title: { paddingHorizontal: 12, paddingTop: 10, fontSize: 12, color: "#2D2F33", ...({ fontFamily: FontFamily.bold } as any) },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    margin: 8,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: "#DEE2E6",
    borderRadius: 5,
  },
  searchInput: { flex: 1, height: 30, fontSize: 12, color: "#2D2F33", ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : {}) },
  actionsRow: { flexDirection: "row", justifyContent: "space-around", alignItems: "center", paddingBottom: 8 },
  actionButton: { paddingHorizontal: 8, paddingVertical: 3 },
  actionText: { fontSize: 12, color: "#0B5FC6", ...({ fontFamily: FontFamily.bold } as any) },
  divider: { width: 1, height: 16, backgroundColor: "#DEE2E6" },
  list: { maxHeight: Dimensions.get("window").height * 0.28 },
  option: { flexDirection: "row", alignItems: "center", gap: 10, paddingHorizontal: 12, paddingVertical: 9 },
  optionSelected: { backgroundColor: "#F0F6FF" },
  checkbox: { width: 18, height: 18, alignItems: "center", justifyContent: "center", borderWidth: 1.5, borderColor: "#ADB5BD", borderRadius: 4 },
  checkboxChecked: { backgroundColor: "#0B5FC6", borderColor: "#0B5FC6" },
  optionText: { flex: 1, fontSize: 12, color: "#343A40", ...({ fontFamily: FontFamily.medium } as any) },
  emptyText: { padding: 16, textAlign: "center", fontSize: 12, color: "#6C757D" },
  footer: { borderTopWidth: 1, borderTopColor: "#E9ECEF", paddingVertical: 8, backgroundColor: "#F8F9FA" },
  footerText: { textAlign: "center", fontSize: 11, color: "#6B6E72", ...({ fontFamily: FontFamily.medium } as any) },
});

export default TeamAssignmentFilterDropdown;
